import { type EnumOption, getOptions } from '@core/utils/enum';
import { toProperCase } from '@core/utils/string';
import type { Vestment } from '../../api/vestment';
import albImage from '../assets/vestment/alb.webp';
import chasubleImage from '../assets/vestment/chasuble.webp';
import mitreImage from '../assets/vestment/mitre.webp';
import stoleImage from '../assets/vestment/stole.webp';
import { AlbType } from '../enum/albType';
import { ChasubleType } from '../enum/chasubleType';
import { StoleType } from '../enum/stoleType';
import { VestmentCategory } from '../enum/vestmentCategory';
import {
  VestmentType,
  fromCategoryAndSubtype,
  getCategory,
  getSubtype,
  isV1VestmentType,
} from '../enum/vestmentType';
import { getAlbFeatures } from './alb';
import { usesVestmentCollars } from './collar';
import {
  type ImageFields,
  getImageFileUrl,
  getImageThumbnailUrl,
} from './image';
import { getStoleFeatures } from './stole';

export const VESTMENT_CATEGORY_IMAGES: Record<VestmentCategory, string> = {
  [VestmentCategory.Chasuble]: chasubleImage,
  [VestmentCategory.Alb]: albImage,
  [VestmentCategory.Stole]: stoleImage,
  [VestmentCategory.Mitre]: mitreImage,
};

export const VESTMENT_CATEGORY_IMAGE_URLS = Object.values(
  VESTMENT_CATEGORY_IMAGES
);

const LEGACY_LABELS: Record<string, string> = {
  [ChasubleType.V1Orphrey]: 'Legacy Orphrey',
  [ChasubleType.V1Plain]: 'Legacy Plain',
  [StoleType.V1Ordinary]: 'Legacy Ordinary',
  [StoleType.V1Special]: 'Legacy Special',
  [StoleType.V1RomanOrdinary]: 'Legacy Roman Ordinary',
  [StoleType.V1RomanSpecial]: 'Legacy Roman Special',
  [StoleType.V1DeaconStraight]: 'Legacy Deacon Straight',
  [StoleType.V1DeaconLShaped]: 'Legacy Deacon L Shaped',
  [AlbType.V1Roman]: 'Legacy Roman',
  [AlbType.V1NonRoman]: 'Legacy Non-Roman',
  [AlbType.V1Surplice]: 'Legacy Surplice',
};

const DESIGN_LABELS: Record<string, string> = {
  [ChasubleType.CenterDesign]: 'Center Design / Patch',
  [StoleType.FlatPriest]: 'Flat / Standing Back Priest',
};

function labelSubtype(subtype: string, labels: Record<string, string>) {
  return labels[subtype] ?? toProperCase(subtype);
}

function chasubleSubtypeOptions(
  include: (type: VestmentType) => boolean,
  labels: Record<string, string>
): EnumOption[] {
  return Object.values(ChasubleType)
    .filter((subtype) =>
      include(fromCategoryAndSubtype(VestmentCategory.Chasuble, subtype))
    )
    .map((subtype) => ({
      value: subtype,
      label: labelSubtype(subtype, labels),
    }));
}

function albSubtypeOptions(
  include: (type: VestmentType) => boolean
): EnumOption[] {
  return Object.values(AlbType)
    .filter((subtype) =>
      include(fromCategoryAndSubtype(VestmentCategory.Alb, subtype))
    )
    .map((subtype) => ({
      value: subtype,
      label:
        subtype === AlbType.RomanSideZipper
          ? 'Roman with Side Zipper'
          : labelSubtype(subtype, LEGACY_LABELS),
    }));
}

function stoleSubtypeOptions(
  include: (type: VestmentType) => boolean,
  labels: Record<string, string>
): EnumOption[] {
  return Object.values(StoleType)
    .filter((subtype) =>
      include(fromCategoryAndSubtype(VestmentCategory.Stole, subtype))
    )
    .map((subtype) => ({
      value: subtype,
      label: labelSubtype(subtype, labels),
    }));
}

function getDesignTypeLabel(type: VestmentType): string {
  if (type === VestmentType.AlbOverlapping) {
    return 'Alb';
  }
  const category = toProperCase(getCategory(type));
  const subtype = getSubtype(type);
  return subtype
    ? `${labelSubtype(subtype, DESIGN_LABELS)} ${category}`
    : category;
}

/**
 * Center Patch reuses Center Design designs, Chasuble-Alb reuses Overlapping
 * designs, and Standing Back Priest reuses Flat Priest designs. An alb owns
 * designs only when its design list is itself.
 */
export function isDesignVestmentType(type: VestmentType): boolean {
  if (isV1VestmentType(type)) {
    return false;
  }
  const alb = getAlbFeatures(type);
  if (alb) {
    return alb.designTypes.length === 1 && alb.designTypes[0] === type;
  }
  return (
    type !== VestmentType.ChasubleCenterPatch &&
    type !== VestmentType.ChasubleAlb &&
    type !== VestmentType.StoleStandingBackPriest
  );
}

export const DESIGN_VESTMENT_TYPE_OPTIONS: EnumOption[] = Object.values(
  VestmentType
)
  .filter(isDesignVestmentType)
  .map((type) => ({ value: type, label: getDesignTypeLabel(type) }));

export const VESTMENT_SUBTYPE_OPTIONS_BY_CATEGORY: Record<
  VestmentCategory,
  EnumOption[]
> = {
  [VestmentCategory.Chasuble]: chasubleSubtypeOptions(
    (type) => !isV1VestmentType(type),
    LEGACY_LABELS
  ),
  [VestmentCategory.Alb]: albSubtypeOptions((type) => !isV1VestmentType(type)),
  [VestmentCategory.Stole]: stoleSubtypeOptions(
    (type) => !isV1VestmentType(type),
    LEGACY_LABELS
  ),
  [VestmentCategory.Mitre]: [],
};

export const DESIGN_SUBTYPE_OPTIONS_BY_CATEGORY: Record<
  VestmentCategory,
  EnumOption[]
> = {
  ...VESTMENT_SUBTYPE_OPTIONS_BY_CATEGORY,
  [VestmentCategory.Chasuble]: chasubleSubtypeOptions(
    isDesignVestmentType,
    DESIGN_LABELS
  ),
  [VestmentCategory.Alb]: [],
  [VestmentCategory.Stole]: stoleSubtypeOptions(
    isDesignVestmentType,
    DESIGN_LABELS
  ),
};

const CHIP_TYPES_WITHOUT_CATEGORY = new Set<VestmentType>([
  VestmentType.ChasubleCenterDesign,
  VestmentType.ChasubleDirectEmbroideryDesign,
  VestmentType.StoleFlatPriest,
]);

export function getDesignChipLabel(type: VestmentType | null): string | null {
  if (type === null) {
    return null;
  }
  const subtype = getSubtype(type);
  return subtype && CHIP_TYPES_WITHOUT_CATEGORY.has(type)
    ? labelSubtype(subtype, DESIGN_LABELS)
    : getDesignTypeLabel(type);
}

export function getSubtypeLabel(subtype: string): string {
  return labelSubtype(subtype, LEGACY_LABELS);
}

export function getVestmentTypeLabel(type: VestmentType): string {
  if (type === VestmentType.ChasubleAlb) {
    return 'Chasuble-Alb';
  }
  if (type === VestmentType.AlbRomanSideZipper) {
    return 'Roman Alb with Side Zipper';
  }
  const category = toProperCase(getCategory(type));
  const subtype = getSubtype(type);
  return subtype ? `${getSubtypeLabel(subtype)} ${category}` : category;
}

const SIZE_STOLE_VESTMENT_TYPES = Object.values(VestmentType).filter(
  (type) =>
    getCategory(type) === VestmentCategory.Stole && !isV1VestmentType(type)
);

const SIZE_STOLE_VESTMENT_TYPE_SET = new Set<VestmentType>(
  SIZE_STOLE_VESTMENT_TYPES
);

export type SizeVestmentValue = {
  vestmentCategory: VestmentCategory;
  vestmentType: VestmentType | null;
};

export const SIZE_VESTMENT_OPTIONS: {
  value: SizeVestmentValue;
  label: string;
}[] = getOptions(VestmentCategory).flatMap(
  (option): { value: SizeVestmentValue; label: string }[] => {
    if (option.value !== VestmentCategory.Stole) {
      return [
        {
          value: {
            vestmentCategory: option.value as VestmentCategory,
            vestmentType: null,
          },
          label: option.label,
        },
      ];
    }
    return SIZE_STOLE_VESTMENT_TYPES.map((type) => ({
      value: {
        vestmentCategory: VestmentCategory.Stole,
        vestmentType: type,
      },
      label: getVestmentTypeLabel(type),
    }));
  }
);

export function sizeVestmentsEqual(
  left: SizeVestmentValue | '',
  right: SizeVestmentValue | ''
): boolean {
  if (left === '' || right === '') {
    return left === right;
  }
  return (
    left.vestmentCategory === right.vestmentCategory &&
    left.vestmentType === right.vestmentType
  );
}

export function isSelectableStoleVestmentType(
  type: string
): type is VestmentType {
  return SIZE_STOLE_VESTMENT_TYPE_SET.has(type as VestmentType);
}

type VestmentImageInput =
  | (Pick<Vestment, 'type'> & ImageFields)
  | null
  | undefined;

/** Collar, stole cut, or alb subtype image when that type uses one, otherwise the vestment image. */
function selectVestmentImage(
  vestment: VestmentImageInput,
  collar?: ImageFields | null,
  stoleCut?: ImageFields | null,
  albSubtype?: ImageFields | null
): ImageFields | null {
  if (usesVestmentCollars(vestment?.type) && collar?.imagePath != null) {
    return collar;
  }
  if (
    getStoleFeatures(vestment?.type)?.hasCuts &&
    stoleCut?.imagePath != null
  ) {
    return stoleCut;
  }
  if (
    getAlbFeatures(vestment?.type)?.hasSubtypes &&
    albSubtype?.imagePath != null
  ) {
    return albSubtype;
  }
  if (vestment?.imagePath != null) {
    return vestment;
  }
  return null;
}

export function getVestmentImageUrls(
  vestment: VestmentImageInput,
  collar?: ImageFields | null,
  stoleCut?: ImageFields | null,
  albSubtype?: ImageFields | null
): { imageUrl: string | null; thumbnailUrl: string | null } {
  const image = selectVestmentImage(vestment, collar, stoleCut, albSubtype);
  return {
    imageUrl: getImageFileUrl(image),
    thumbnailUrl: getImageThumbnailUrl(image),
  };
}

export function getVestmentImageUrl(
  vestment: VestmentImageInput,
  collar?: ImageFields | null,
  stoleCut?: ImageFields | null,
  albSubtype?: ImageFields | null
): string | null {
  return getVestmentImageUrls(vestment, collar, stoleCut, albSubtype).imageUrl;
}
